<script setup lang="ts">
import { onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import SpeedDial from "primevue/speeddial";
import Button from "primevue/button";
import { getDisplayName } from "@persolog/utils";

// Presentational: reads/sets the active locale via the app's global i18n
// instance (useI18n), and emits `change` so each app persists the choice its
// own way (preferences store, localStorage, …). `v-tooltip` resolves to the
// app's globally-registered PrimeVue Tooltip directive (as in the originals).
const { t, locale, availableLocales } = useI18n({ useScope: "global" });

// `locales` narrows the offered languages (the questionnaire app limits them to
// the languages its questionnaire exists in, which only arrive with the api
// payloads — hence computed, not built once). Without it, every app locale is
// offered, as before.
const props = defineProps<{ locales?: string[] }>();

const emit = defineEmits<{ change: [lang: string] }>();

const items = computed(() =>
    (props.locales ?? availableLocales).map((lang) => ({
        label: lang.toUpperCase(),
        selected: computed(() => locale.value.toUpperCase() === lang.toUpperCase()),
        command: () => emit("change", lang),
    })),
);

onMounted(() => {
    const storedLang = localStorage.getItem("selectedLanguage");
    if (storedLang && availableLocales.includes(storedLang)) {
        locale.value = storedLang;
    }
});
</script>

<template>
    <SpeedDial
        id="language-selection_speed-dial"
        :model="items"
        show-icon="pi pi-globe"
        type="linear"
        direction="down"
        :style="{ position: 'absolute', top: '-1.25rem' }"
        button-class="p-button-icon-only p-button-text"
        :aria-label="t('accessibility.buttons.languageSelection')"
    >
        <template #item="{ item, onClick }">
            <Button
                :id="'language-selection_btn_' + item.label"
                @click="onClick"
                tabindex="-1"
                rounded
                :aria-label="getDisplayName(String(item.label), String(item.label))"
                v-tooltip.left="getDisplayName(String(item.label), String(item.label))"
                :class="item.selected ? 'bg-brand text-white' : 'text-brand bg-light'"
                :pt="{
                    root: {
                        style: {
                            border: 'none',
                            fontSize: '0.75em',
                            padding: '0.75em',
                            minWidth: '3em',
                            fontWeight: 'bold',
                            background: item.selected ? 'var(--p-primary-500)' : 'var(--p-primary-50)',
                        },
                    },
                }"
            >
                <span class="lang">{{ item.label }}</span>
            </Button>
        </template>
    </SpeedDial>
</template>

<style scoped>
.bg-light {
    background-color: var(--p-surface-0);
}

Button:active .lang,
Button:hover .lang {
    color: var(--p-surface-0);
}

/* The linear/direction-down SpeedDial reserves vertical space for its (hidden)
   language list, so while closed its box hangs below the header over the page
   content and would swallow clicks meant for elements underneath it. When
   closed, let clicks pass through everything except the toggle button.
   Targeted globally by id: PrimeVue's SpeedDial root has inheritAttrs:false, so
   it does NOT carry this component's scoped data-v attribute — a scoped
   `.p-speeddial` selector would never match it. */
:global(#language-selection_speed-dial:not(.p-speeddial-open)) {
    pointer-events: none;
}

:global(#language-selection_speed-dial:not(.p-speeddial-open) .p-speeddial-list) {
    pointer-events: none;
}

:global(#language-selection_speed-dial:not(.p-speeddial-open) .p-speeddial-button) {
    pointer-events: auto;
}
</style>
